import React from "react";
import { Link } from "react-router-dom";
import { PACKAGE_IMAGES } from "./packageImages";

const WEBSITE_ROWS = [
  { id: "bow-tie-lite", name: "Bow Tie Lite Website Package", body: "A clean, conversion-ready starter site that captures leads from day one. Hosting, SSL, and a contact form are wired up so you can launch credibly without juggling tools.", bullets: ["1–3 page custom build", "Hosting + SSL + backups", "Basic on-page SEO", "2 revisions / month"] },
  { id: "classic-knot", name: "Classic Knot (Standard Website)", body: "Adds pages, structure, and light analytics to support growth. Once traffic increases, clarity and flow start to matter more — you get a site that supports bookings, tells your story, and gives you visibility into performance.", bullets: ["Up to 5 custom pages", "Booking / calendar integration", "GA4 analytics setup", "2 revisions / month"], reverse: true },
  { id: "windsor-growth", name: "Windsor Growth (Unlimited Pages)", body: "A growth-focused build with more pages, optimization, and unlimited revisions. The practical result: a site that evolves as your offers, messaging, and strategy mature.", bullets: ["Up to 10 custom pages", "Performance + conversion tuning", "Unlimited revisions*", "Hosting + SSL included"] },
  { id: "black-label-studio", name: "Black Label Studio (Unlimited + eCommerce)", body: "Premium done-for-you build with full eCommerce and priority support. You get a high-capacity site that handles products, performance, and changes without bottlenecks.", bullets: ["Up to 15 custom pages", "Full eCommerce (100 products)", "Priority queue support", "Unlimited revisions"], reverse: true },
];

export default function PackageShowcase({ rows = WEBSITE_ROWS, imagesMap = PACKAGE_IMAGES, sectionId = "packages", overline = "Package" }) {
  return (
    <section id={sectionId} data-testid="package-showcase" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
        {rows.map((row) => (
          <div
            key={row.id}
            data-testid={`package-row-${row.id}`}
            className={`grid lg:grid-cols-12 gap-10 md:gap-16 items-center ${row.reverse ? "lg:[&>div:first-child]:order-2" : ""}`}
          >
            <div className="lg:col-span-7">
              <span className="text-[11px] uppercase tracking-[0.28em] text-gold/80">
                {overline} · {row.id.split("-")[0]}
              </span>
              <h3 className="mt-4 font-display text-3xl md:text-5xl leading-tight">{row.name}</h3>
              <p className="mt-5 text-white/65 text-base md:text-lg leading-relaxed max-w-xl">{row.body}</p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl">
                {row.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/75">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <Link
                to={`/websites/${row.id}`}
                data-testid={`package-details-${row.id}`}
                className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gold hover:text-white transition-colors"
              >
                See full details →
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="relative max-w-md mx-auto animate-float">
                <div className="absolute -inset-8 -z-10 rounded-[2rem] bg-gradient-to-br from-gold/12 via-transparent to-transparent blur-3xl" />
                <img
                  src={imagesMap[row.id]}
                  alt={`${row.name} package`}
                  data-testid={`package-image-${row.id}`}
                  className="w-full h-auto rounded-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
